import { CheckIcon } from "@radix-ui/react-icons";
import { useInfiniteQuery } from "@tanstack/react-query";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ClipLoader } from "react-spinners";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { compareStrings } from "@/lib/utils";
import { DEFAULT_GRID_PAGE_SIZE } from "@/shared/constants/common";
import { KEY } from "@/shared/constants/keyboard";
import { UseData } from "./UseData";

const DEFAULT_PAGE_SIZE = DEFAULT_GRID_PAGE_SIZE;

export interface ITickerValue {
	changed: string;
	selected: string;
}

export function Ticker({
	ticker,
	onChange,
	height,
	placeholder = "Search ticker...",
}: {
	ticker?: string;
	onChange?: (value: string) => void;
	height?: number;
	placeholder?: string;
}) {
	const { execute } = UseData.create().useGetData();
	const [value, setValue] = useState<ITickerValue>({
		changed: "",
		selected: ticker ?? "",
	});
	const [isPopoverContentVisible, setIsPopoverContentVisible] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);

	const inputRef = useRef<HTMLInputElement>(null);
	const commandItemRefs = useRef<HTMLDivElement[]>([]);
	const { ref, inView } = useInView();

	const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
		useInfiniteQuery({
			queryKey: ["Security", value.changed],
			queryFn: async ({ pageParam }) => {
				const result = await execute({
					text: value.changed,
					offset: pageParam,
					limit: DEFAULT_PAGE_SIZE,
				});
				return {
					data: result?.data || [],
					hasMore: result?.metadata?.pagination?.nextPage,
				};
			},
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				if (lastPage?.hasMore) {
					return (allPages?.length ?? 0) * DEFAULT_PAGE_SIZE;
				}
				return null;
			},
			select: (data) => {
				const flatData = data.pages.flatMap((x) => x.data);

				const result = {
					pages: flatData,
				};

				return result;
			},
		});

	useEffect(() => {
		setValue({
			changed: ticker ?? "",
			selected: ticker ?? "",
		});
		setHighlightedIndex(0);
	}, [ticker]);

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

	useEffect(() => {
		if (
			highlightedIndex !== null &&
			commandItemRefs.current[highlightedIndex]
		) {
			commandItemRefs.current[highlightedIndex].scrollIntoView({
				block: "nearest",
			});
		}
	}, [highlightedIndex]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (data?.pages && data.pages.length > 0) {
			if (e.key === KEY.ARROW_DOWN) {
				e.preventDefault();
				setHighlightedIndex((prev) => {
					const nextIndex = prev === null ? 0 : prev + 1;
					if (
						nextIndex >= data.pages.length - 1 &&
						hasNextPage &&
						!isFetchingNextPage
					) {
						fetchNextPage();
					}
					return nextIndex < data.pages.length ? nextIndex : prev;
				});
			} else if (e.key === KEY.ARROW_UP) {
				e.preventDefault();
				setHighlightedIndex((prev) =>
					prev === null || prev <= 0 ? 0 : prev - 1,
				);
			} else if (e.key === KEY.ENTER) {
				const selectedTicker = data.pages[highlightedIndex];
				if (selectedTicker && selectedTicker.ticker !== value.selected) {
					setValue({
						changed: selectedTicker.ticker,
						selected: selectedTicker.ticker,
					});
					if (onChange) {
						onChange(selectedTicker.ticker);
					}

					setIsPopoverContentVisible(false);
				}
			}
		}
	};

	const setItemRef = (index: number) => (el: HTMLDivElement | null) => {
		if (el) {
			commandItemRefs.current[index] = el;
		}
	};

	return (
		<Popover open={true}>
			<PopoverTrigger asChild>
				<Input
					className="bg-background w-[106px]"
					type="text"
					placeholder={placeholder}
					ref={inputRef}
					value={value.changed}
					onClick={(e) => {
						e.preventDefault();
						setIsPopoverContentVisible((prev) => !prev);
					}}
					onBlur={() => setIsPopoverContentVisible(false)}
					onChange={(e) => {
						setValue({ ...value, changed: e.target.value });
						setHighlightedIndex(0);
						setIsPopoverContentVisible(true);
					}}
					onFocus={() => {
						inputRef.current?.focus();
						inputRef.current?.select();
					}}
					onKeyDown={handleKeyDown}
					style={{
						height: height ? `${height}px` : "auto",
					}}
				/>
			</PopoverTrigger>
			{isPopoverContentVisible && (
				<PopoverContent
					className="w-[280px] p-0"
					onOpenAutoFocus={(e) => {
						e.preventDefault();
					}}
					onCloseAutoFocus={(e) => {
						e.preventDefault();
						inputRef.current?.focus();
						inputRef.current?.select();
					}}
				>
					<Command>
						{isLoading ? (
							<ClipLoader
								className="my-4 self-center"
								size={20}
								color="var(--gray)"
							/>
						) : data?.pages.length !== 0 && !isLoading ? (
							<CommandGroup>
								<CommandList>
									{data?.pages.map((item, index) => (
										<CommandItem
											key={`${item.ticker}-${index}`}
											keywords={[item.name as string]}
											className={cn("cursor-pointer", {
												"bg-(--dark-background)":
													index === highlightedIndex ||
													compareStrings(value.selected, item.ticker) === 0,
											})}
											ref={setItemRef(index)}
											value={item.ticker}
											onSelect={(currentValue) => {
												if (currentValue !== value.selected) {
													setValue({
														changed: currentValue,
														selected: currentValue,
													});
													if (onChange) {
														onChange(currentValue);
													}
													setIsPopoverContentVisible(false);
												}
											}}
											onMouseDown={(e) => {
												e.preventDefault();
											}}
										>
											<p className="mr-1">
												<span className="text-gold font-bold">
													{item.ticker}
												</span>{" "}
												<span className="text-neutral-400">{item.name}</span>
											</p>
											<CheckIcon
												className={cn(
													"ml-auto w-4 text-neutral-400 shrink-0",
													compareStrings(value.selected, item.ticker) === 0
														? "opacity-100"
														: "opacity-0",
												)}
											/>
										</CommandItem>
									))}
									<div ref={ref} className="flex justify-center">
										{isFetchingNextPage && (
											<ClipLoader size={20} color="var(--gray)" />
										)}
									</div>
								</CommandList>
							</CommandGroup>
						) : (
							<CommandEmpty>Not found.</CommandEmpty>
						)}
					</Command>
				</PopoverContent>
			)}
		</Popover>
	);
}
